import { inject, Injectable, OnDestroy } from '@angular/core';
import {
  PublicDedicatedCalendar,
  ThemeMode,
  BackgroundPattern,
  BorderRadius,
  ShadowIntensity,
  ContainerWidth,
  SectionSpacing,
} from '../models/public-calendar.models';
import { PublicI18nService } from './public-i18n.service';

/**
 * Service to apply theme CSS variables from dedicated calendar settings.
 */
@Injectable({
  providedIn: 'root',
})
export class ThemeService implements OnDestroy {
  private readonly i18n = inject(PublicI18nService);
  private systemPreferenceListener: ((e: MediaQueryListEvent) => void) | null = null;
  private currentThemeMode: ThemeMode = 'system';

  /** Maps border radius values to CSS units */
  private readonly radiusMap: Record<BorderRadius, string> = {
    none: '0',
    sm: '0.25rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    full: '9999px',
  };

  /** Maps shadow intensity to CSS box-shadow values */
  private readonly shadowMap: Record<ShadowIntensity, string> = {
    none: 'none',
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
  };

  /** Maps container width to CSS max-width values */
  private readonly containerWidthMap: Record<ContainerWidth, string> = {
    narrow: '768px',
    default: '1024px',
    wide: '1280px',
    full: '100%',
  };

  /** Maps section spacing to CSS padding values */
  private readonly sectionSpacingMap: Record<SectionSpacing, string> = {
    compact: '2rem',
    default: '4rem',
    relaxed: '6rem',
  };

  /**
   * Apply theme from hex color values to CSS custom properties.
   * Works with any object that has the required color fields (SectionTheme, PublicDedicatedCalendar, etc.)
   */
  applyThemeColors(colors: {
    primaryColor: string;
    secondaryColor: string;
    accentColor: string;
    backgroundColor: string;
    surfaceColor: string;
    textColor: string;
    textMutedColor: string;
    linkColor: string;
    linkHoverColor: string;
    buttonTextColor: string;
    borderColor: string;
    borderRadius?: string;
    fontFamily?: string;
  }): void {
    const root = document.documentElement;
    root.style.setProperty('--color-primary', this.hexToRgb(colors.primaryColor));
    root.style.setProperty('--color-secondary', this.hexToRgb(colors.secondaryColor));
    root.style.setProperty('--color-accent', this.hexToRgb(colors.accentColor));
    root.style.setProperty('--color-background', this.hexToRgb(colors.backgroundColor));
    root.style.setProperty('--color-surface', this.hexToRgb(colors.surfaceColor));
    root.style.setProperty('--color-foreground', this.hexToRgb(colors.textColor));
    root.style.setProperty('--color-muted', this.hexToRgb(colors.textMutedColor));
    root.style.setProperty('--color-link', this.hexToRgb(colors.linkColor));
    root.style.setProperty('--color-link-hover', this.hexToRgb(colors.linkHoverColor));
    root.style.setProperty('--color-button-text', this.hexToRgb(colors.buttonTextColor));
    root.style.setProperty('--color-border', this.hexToRgb(colors.borderColor));
    if (colors.borderRadius) {
      root.style.setProperty('--radius', colors.borderRadius);
    }
    if (colors.fontFamily) {
      root.style.setProperty('--font-family', colors.fontFamily);
      document.body.style.fontFamily = 'var(--font-family)';
    }
  }

  /**
   * Apply theme from dedicated calendar to CSS custom properties.
   */
  applyTheme(calendar: PublicDedicatedCalendar): void {
    const root = document.documentElement;

    // Convert hex colors to RGB values for CSS custom properties
    root.style.setProperty('--color-primary', this.hexToRgb(calendar.primaryColor));
    root.style.setProperty('--color-secondary', this.hexToRgb(calendar.secondaryColor));
    root.style.setProperty('--color-accent', this.hexToRgb(calendar.accentColor));
    root.style.setProperty('--color-background', this.hexToRgb(calendar.backgroundColor));
    root.style.setProperty('--color-surface', this.hexToRgb(calendar.surfaceColor));
    root.style.setProperty('--color-foreground', this.hexToRgb(calendar.textColor));
    root.style.setProperty('--color-muted', this.hexToRgb(calendar.textMutedColor));

    // Extended theme colors
    root.style.setProperty('--color-link', this.hexToRgb(calendar.linkColor));
    root.style.setProperty('--color-link-hover', this.hexToRgb(calendar.linkHoverColor));
    root.style.setProperty('--color-button-text', this.hexToRgb(calendar.buttonTextColor));
    root.style.setProperty('--color-border', this.hexToRgb(calendar.borderColor));

    // Border radius (general)
    root.style.setProperty('--radius', this.radiusMap[calendar.borderRadius] || '0.5rem');

    // Button-specific border radius
    root.style.setProperty('--radius-button', this.radiusMap[calendar.buttonRadius] || '0.5rem');

    // Shadow intensity
    root.style.setProperty('--shadow', this.shadowMap[calendar.shadowIntensity] || this.shadowMap['md']);

    // Container width
    root.style.setProperty('--container-width', this.containerWidthMap[calendar.containerWidth] || '1024px');

    // Section spacing
    root.style.setProperty('--section-spacing', this.sectionSpacingMap[calendar.sectionSpacing] || '4rem');

    // Font family
    const fontMap: Record<string, string> = {
      system: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      inter: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      roboto: '"Roboto", -apple-system, BlinkMacSystemFont, sans-serif',
      'open-sans': '"Open Sans", -apple-system, BlinkMacSystemFont, sans-serif',
      poppins: '"Poppins", -apple-system, BlinkMacSystemFont, sans-serif',
      'sans-serif': '"Helvetica Neue", Helvetica, Arial, sans-serif',
      playfair: '"Playfair Display", Georgia, Cambria, serif',
      serif: 'Georgia, Cambria, "Times New Roman", Times, serif',
      monospace: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    };
    root.style.setProperty('--font-family', fontMap[calendar.fontFamily] || fontMap['system']);
    document.body.style.fontFamily = 'var(--font-family)';

    // Apply theme mode (light/dark/system)
    this.applyThemeMode(calendar.themeMode);

    // Apply background pattern - temporarily disabled
    // this.applyBackgroundPattern(calendar.backgroundPattern);

    // Update page title with company name
    document.title = `${this.i18n.t('booking.requestTitle')}${calendar.companyName}`;
  }

  /**
   * Apply theme mode to document root (handles system preference).
   */
  applyThemeMode(mode: ThemeMode): void {
    this.currentThemeMode = mode;
    const root = document.documentElement;

    // Clean up existing listener
    this.removeSystemPreferenceListener();

    if (mode === 'dark') {
      root.classList.add('dark');
    } else if (mode === 'light') {
      root.classList.remove('dark');
    } else {
      // System mode: check user preference and listen for changes
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
      this.setupSystemPreferenceListener();
    }
  }

  /**
   * Apply background pattern to document root.
   */
  applyBackgroundPattern(pattern: BackgroundPattern): void {
    const root = document.documentElement;

    // Remove existing pattern attribute
    delete root.dataset['pattern'];

    // Apply new pattern if not 'none'
    if (pattern && pattern !== 'none') {
      root.dataset['pattern'] = pattern;
    }
  }

  /**
   * Set up listener for system color scheme changes.
   */
  private setupSystemPreferenceListener(): void {
    this.systemPreferenceListener = (e: MediaQueryListEvent) => {
      const root = document.documentElement;
      if (e.matches) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    };

    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', this.systemPreferenceListener);
  }

  /**
   * Remove system preference listener.
   */
  private removeSystemPreferenceListener(): void {
    if (this.systemPreferenceListener) {
      window.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', this.systemPreferenceListener);
      this.systemPreferenceListener = null;
    }
  }

  ngOnDestroy(): void {
    this.removeSystemPreferenceListener();
  }

  /**
   * Reset theme to defaults (used when preview component is destroyed).
   */
  resetTheme(): void {
    const root = document.documentElement;

    // Remove custom properties - core colors
    root.style.removeProperty('--color-primary');
    root.style.removeProperty('--color-secondary');
    root.style.removeProperty('--color-accent');
    root.style.removeProperty('--color-background');
    root.style.removeProperty('--color-surface');
    root.style.removeProperty('--color-foreground');
    root.style.removeProperty('--color-muted');

    // Remove extended theme colors
    root.style.removeProperty('--color-link');
    root.style.removeProperty('--color-link-hover');
    root.style.removeProperty('--color-button-text');
    root.style.removeProperty('--color-border');

    // Remove layout properties
    root.style.removeProperty('--radius');
    root.style.removeProperty('--radius-button');
    root.style.removeProperty('--shadow');
    root.style.removeProperty('--container-width');
    root.style.removeProperty('--section-spacing');
    root.style.removeProperty('--font-family');

    // Reset body font
    document.body.style.fontFamily = '';

    // Remove dark mode class
    root.classList.remove('dark');

    // Remove pattern
    delete root.dataset['pattern'];

    // Clean up listener
    this.removeSystemPreferenceListener();
  }

  /**
   * Convert hex color (#RRGGBB) to RGB values string (R G B).
   */
  private hexToRgb(hex: string): string {
    // Remove # if present
    const cleanHex = hex.replace('#', '');

    // Parse RGB values
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);

    return `${r} ${g} ${b}`;
  }
}
